import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';

export type StatAccent = 'lime' | 'aqua' | 'pink' | 'orange' | 'neutral';

// The icon chip tint per accent. Kept subtle (a soft token wash behind the
// icon) so it reads as a category cue, never a loud fill — lime stays spice.
const accentChip: Record<StatAccent, string> = {
  lime: 'bg-[color-mix(in_srgb,var(--lime)_28%,transparent)] text-foreground',
  aqua: 'bg-[color-mix(in_srgb,var(--aqua)_40%,transparent)] text-foreground',
  pink: 'bg-[color-mix(in_srgb,var(--pink)_40%,transparent)] text-foreground',
  orange: 'bg-[color-mix(in_srgb,var(--orange)_32%,transparent)] text-foreground',
  neutral: 'bg-muted text-foreground/70',
};

// StatCard — a KPI tile: an icon chip + label eyebrow, a big number, an optional
// sub-line, an optional trailing element (usually a DeltaChip), and an optional
// children slot below the value (usually a Sparkline). When `to` is set the whole
// tile is a link that lifts on hover. Token-driven and dark-mode aware.
//
// Shared primitive generalised from the billing InvoiceSummaryPanel tile.
export function StatCard({
  icon,
  label,
  value,
  sub,
  trailing,
  accent = 'neutral',
  to,
  onClick,
  ariaLabel,
  children,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  trailing?: ReactNode;
  accent?: StatAccent;
  to?: string;
  // onClick makes the tile a button instead of a link — used where the tile
  // opens a detail dialog rather than navigating. Takes precedence over `to`,
  // which then belongs on a link INSIDE the dialog: a tile can't be both a
  // link and a button without breaking keyboard semantics.
  onClick?: () => void;
  ariaLabel?: string;
  children?: ReactNode;
}) {
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${accentChip[accent]}`}>
          {icon}
        </span>
        {trailing ??
          (to || onClick ? (
            <ArrowRight size={16} weight="bold" className="text-muted-foreground" aria-hidden />
          ) : null)}
      </div>
      <div className="mt-3 flex flex-col gap-0.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="font-heading text-2xl font-bold tabular-nums tracking-tight md:text-3xl">{value}</span>
        {sub && <span className="text-sm text-muted-foreground">{sub}</span>}
      </div>
      {children && <div className="mt-2">{children}</div>}
    </>
  );

  const base =
    'flex flex-col rounded-2xl border border-border bg-card p-4 shadow-sm transition';
  const interactive = 'hover:-translate-y-0.5 hover:shadow-md motion-reduce:hover:translate-y-0';
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={`${base} ${interactive} cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lime,#BEE034)]`}
      >
        {inner}
      </button>
    );
  }
  if (to) {
    return (
      <Link
        to={to}
        className={`${base} hover:-translate-y-0.5 hover:shadow-md motion-reduce:hover:translate-y-0`}
      >
        {inner}
      </Link>
    );
  }
  return <div className={base}>{inner}</div>;
}
